import { useState } from "react";
import { Box, Typography } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import WbCloudyOutlinedIcon from "@mui/icons-material/WbCloudyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { WorkshopSession } from "../../types/workshopSession";
import { VehicleImage } from "./vehicleImage";
import { LightBox } from "./lightBox";
import { BubbleLoadingWidget } from "./bubbleLoadingWidget";
import View from "../atoms/view";
import { Card } from "../atoms/card";
import { P, TextRegular } from "../atoms/text";
import { Spacing } from "../../constants/theme";
import { InfoSummary } from "./infoSummary";

const formatDate = (date?: string | null) => {
  if (!date) return "—";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toISOString().slice(0, 10);
};

export const InspectionPhotos = ({
  currentWorkshopSession,
}: {
  currentWorkshopSession: WorkshopSession | null;
}) => {
  // Just take the first inspection from the session since all inspections 
  // in a work session belong to the same vehicle, regardless of VIN mismatch
  const inspection = currentWorkshopSession?.inspections?.[0];
  const images = inspection?.images ?? [];
  const imageIdsKey = images.map((img) => img.imageId).join(",");

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [loadState, setLoadState] = useState({ key: "", count: 0 });

  // Use the inspection's vehicleVin for the loadKey since that's what's used in the image path
  const loadKey = `${inspection?.vehicleVin ?? ""}:${imageIdsKey}`;
  const loadedCount = loadState.key === loadKey ? loadState.count : 0;

  const sessionLoading = currentWorkshopSession === null;
  const imagesLoading = images.length > 0 && loadedCount < images.length;
  const isLoading = sessionLoading || imagesLoading;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 * Spacing.gap }}>
      {sessionLoading ? (
        <BubbleLoadingWidget text="Loading images..." />
      ) : !inspection ? (
        <View extend={styles.empty}>
          <P>No inspection reported</P>
        </View>
      ) : (
        <>
          <InfoSummary
            items={[
              {
                icon: <AssignmentOutlinedIcon />,
                label: "Inspection type",
                value: "Check in",
              },
              {
                icon: <CalendarTodayOutlinedIcon />,
                label: "Date",
                value: formatDate(inspection?.created),
              },
              {
                icon: <WbCloudyOutlinedIcon />,
                label: "Weather condition",
                value: inspection?.weatherCondition,
              },
              // {
              //   icon: <PersonOutlineIcon />,
              //   label: "Inspector",
              //   value: undefined,
              // },
            ]}
          />

          {isLoading ? <BubbleLoadingWidget text="Loading images..." /> : null}

          <Box
            sx={{
              display: isLoading ? "none" : "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 4,
            }}
          >
            {images.map((i, index) => (
              <Card key={i.imageId} extend={{ height: "auto", overflow: "hidden", padding: 0 }}>
                <Box
                  onClick={() => {
                    setLightboxIndex(index);
                    setLightboxOpen(true);
                  }}
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  <VehicleImage
                    blobId={i.imageId}
                    vehicleVin={inspection?.vehicleVin}
                    onDone={() =>
                      setLoadState((prev) => ({
                        key: loadKey,
                        count: (prev.key === loadKey ? prev.count : 0) + 1,
                      }))
                    }
                  />
                </Box>
                <Box sx={{ padding: " 0 0 12px 12px" }}>
                  <TextRegular>{i.section}</TextRegular>
                  <TextRegular>{i.position}</TextRegular>
                </Box>
              </Card>
            ))}
          </Box>

          {!isLoading && lightboxOpen && images.length ? (
            <LightBox
              imageIds={images.map((img) => img.imageId)}
              vehicleVin={inspection?.vehicleVin}
              open={lightboxOpen}
              initialIndex={lightboxIndex}
              onClose={() => setLightboxOpen(false)}
              onIndexChange={setLightboxIndex}
            />
          ) : null}
        </>
      )}
    </Box>
  );
};

const styles = {
  empty: {
    padding: "24px",
  },
};
